import { addDays, startOfDay } from "../../../../../../../../../../../_shared/_logic/date-math"

export type CalendarDateRange = {
  start: Date | null
  end: Date | null
}

export type CalendarDateRangeInput =
  | Partial<CalendarDateRange>
  | { rangeStart?: Date | string | number | null; rangeEnd?: Date | string | number | null }
  | [Date | string | number | null | undefined, Date | string | number | null | undefined]
  | null
  | undefined

export const DEFAULT_CALENDAR_RANGE_DAYS = 21

const coerceDate = (value?: Date | string | number | null): Date | null => {
  if (!value) return null

  const parsed = value instanceof Date ? value : new Date(value)

  return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed)
}

export const createDefaultCalendarDateRange = (reference = new Date()): CalendarDateRange => {
  const start = startOfDay(reference)

  return {
    start,
    end: addDays(start, DEFAULT_CALENDAR_RANGE_DAYS),
  }
}

export const coerceCalendarDateRangeInput = (value?: CalendarDateRangeInput): CalendarDateRange => {
  if (!value) return { start: null, end: null }

  if (Array.isArray(value)) {
    return {
      start: coerceDate(value[0] ?? null),
      end: coerceDate(value[1] ?? null),
    }
  }

  if ("rangeStart" in value || "rangeEnd" in value) {
    return {
      start: coerceDate(value.rangeStart ?? null),
      end: coerceDate(value.rangeEnd ?? null),
    }
  }

  const range = value as Partial<CalendarDateRange>

  return {
    start: coerceDate(range.start ?? null),
    end: coerceDate(range.end ?? null),
  }
}

export const normalizeCalendarDateRange = (
  value?: Partial<CalendarDateRange> | null,
  fallback: CalendarDateRange = createDefaultCalendarDateRange(),
): CalendarDateRange => {
  const fallbackStart = coerceDate(fallback.start) ?? startOfDay(new Date())
  const fallbackEnd = coerceDate(fallback.end) ?? fallbackStart
  const start = coerceDate(value?.start ?? null)
  const end = coerceDate(value?.end ?? null)

  if (!start && !end) {
    return { start: fallbackStart, end: fallbackEnd }
  }

  if (!start && end) {
    return { start: end, end }
  }

  if (start && !end) {
    return { start, end: start }
  }

  if (start && end && end < start) {
    return { start: end, end: start }
  }

  return { start: start ?? fallbackStart, end: end ?? fallbackEnd }
}

export const calendarRangeVisibleDays = (range: CalendarDateRange): Date[] => {
  const normalized = normalizeCalendarDateRange(range)
  const start = normalized.start ?? startOfDay(new Date())
  const end = normalized.end ?? start
  const days: Date[] = []
  for (let cursor = start; cursor <= end; cursor = addDays(cursor, 1)) {
    days.push(cursor)
  }

  return days
}

export const calendarRangeExclusiveEnd = (range: CalendarDateRange): Date => {
  const normalized = normalizeCalendarDateRange(range)

  return addDays(normalized.end ?? normalized.start ?? startOfDay(new Date()), 1)
}

export const calendarDateRangeKey = (range: CalendarDateRange): string => {
  const normalized = normalizeCalendarDateRange(range)

  return `${normalized.start?.toISOString() ?? ""}|${normalized.end?.toISOString() ?? ""}`
}
